import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  createSettingItem,
  deleteSettingItem,
  fetchSettings,
  selectSettingsGroup,
  updateSettingItem,
} from '@main/features/settings/settingsSlice';
import { UiButton, UiInput, UiNativeSelect } from '@shared/common/ui';

const GROUP = 'tariffs';

const TYPES = [
  { value: 1, label: 'Период' },
  { value: 2, label: 'Количество занятий' },
];

const COUNT_UNITS = [
  { value: 1, label: 'Дней' },
  { value: 2, label: 'Месяцев' },
];

const START_TYPES = [
  { value: 1, label: 'С начала месяца' },
  { value: 2, label: 'Со дня оплаты' },
];

const DEFAULT = {
  name: '',
  type: 1,
  count: 30,
  countUnit: 1,
  startType: 2,
};

export function TariffsSettings() {
  const dispatch = useDispatch();
  const { items, status, saveError, saveStatus } = useSelector(selectSettingsGroup(GROUP));
  const [editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchSettings(GROUP));
  }, [dispatch, status]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: DEFAULT });

  const typeWatch = Number(watch('type'));

  const onSubmit = async (values) => {
    const value = {
      name: values.name.trim(),
      type: Number(values.type),
      count: Number(values.count),
      countUnit: Number(values.countUnit),
      startType: Number(values.startType),
    };
    const action = editingKey
      ? updateSettingItem({ group: GROUP, key: editingKey, value })
      : createSettingItem({ group: GROUP, value });
    const result = await dispatch(action);
    if (result.meta.requestStatus === 'fulfilled') {
      reset(DEFAULT);
      setEditingKey(null);
    }
  };

  const onEdit = (item) => {
    setEditingKey(item.key);
    reset({
      name: item.value?.name ?? '',
      type: item.value?.type ?? 1,
      count: item.value?.count ?? 1,
      countUnit: item.value?.countUnit ?? 1,
      startType: item.value?.startType ?? 2,
    });
  };

  const onCancel = () => {
    setEditingKey(null);
    reset(DEFAULT);
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Удалить тариф «${item.value?.name ?? item.key}»?`)) return;
    await dispatch(deleteSettingItem({ group: GROUP, key: item.key }));
  };

  return (
    <div className="form-grid" style={{ alignItems: 'start' }}>
      <div className="form-card">
        <h2>{editingKey ? 'Редактирование тарифа' : 'Новый тариф'}</h2>
        <p className="muted">Цена тарифа устанавливается в направлении, а не здесь.</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <UiInput
            label="Название"
            type="text"
            error={errors.name?.message}
            {...register('name', {
              required: 'Введите название',
              maxLength: { value: 255, message: 'Слишком длинное название' },
            })}
          />

          <div className="form-grid" style={{ gap: 12 }}>
            <UiNativeSelect label="Тип" {...register('type')}>
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
            </UiNativeSelect>
            <UiInput
              label={typeWatch === 2 ? 'Кол-во занятий' : 'Длительность'}
              type="number"
              min={1}
              error={errors.count?.message}
              {...register('count', {
                required: 'Укажите число',
                min: { value: 1, message: 'Должно быть больше 0' },
              })}
            />
            {typeWatch === 1 && (
              <UiNativeSelect label="Ед. периода" {...register('countUnit')}>
                  {COUNT_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
              </UiNativeSelect>
            )}
          </div>

          {typeWatch === 1 && (
            <UiNativeSelect label="Начало действия" {...register('startType')}>
                {START_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
            </UiNativeSelect>
          )}

          {saveError && <div className="banner banner--danger">{saveError}</div>}

          <div className="form-actions">
            {editingKey && (
              <UiButton variant="ghost" type="button"  onClick={onCancel}>
                Отмена
              </UiButton>
            )}
            <UiButton
              type="submit"
              
              disabled={isSubmitting || saveStatus === 'loading'}
            >
              {editingKey ? 'Сохранить' : 'Добавить тариф'}
            </UiButton>
          </div>
        </form>
      </div>

      <div>
        <h2 style={{ marginTop: 0 }}>Тарифы</h2>
        {items.length === 0 ? (
          <div className="placeholder">Пока нет тарифов</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Параметры</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.key}>
                  <td>{item.value?.name ?? '—'}</td>
                  <td>{describeType(item.value)}</td>
                  <td className="muted">{describeParams(item.value)}</td>
                  <td>
                    <div className="row-actions">
                      <UiButton variant="ghost" size="sm"
                        type="button"
                        
                        onClick={() => onEdit(item)}
                      >
                        Изменить
                      </UiButton>
                      <UiButton variant="danger" size="sm"
                        type="button"
                        
                        onClick={() => onDelete(item)}
                      >
                        Удалить
                      </UiButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function describeType(value) {
  return TYPES.find((t) => t.value === Number(value?.type))?.label ?? '—';
}

function describeParams(value) {
  if (!value) return '';
  if (Number(value.type) === 2) return `${value.count} занятий`;
  const unit = COUNT_UNITS.find((u) => u.value === Number(value.countUnit))?.label ?? '';
  const start =
    START_TYPES.find((s) => s.value === Number(value.startType))?.label ?? '';
  return `${value.count} ${unit}${start ? ' · ' + start : ''}`;
}
